/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.ui.scrolling

import kotlinx.browser.document
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import org.w3c.dom.Element
import org.w3c.dom.HTMLDivElement
import systems.frictionless.impulse.browser.style.AppStyleSheet

/**
 * Reference to https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserverEntry
 */
external class IntersectionObserverEntry {
    val isIntersecting: Boolean
    val target: Element
}

/**
 * Reference to https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 */
@Suppress("UnusedPrivateMember") // https://github.com/detekt/detekt/issues/4340
external class IntersectionObserver(
    callback: (entries: Array<IntersectionObserverEntry>, observer: IntersectionObserver) -> Unit
) {
    fun observe(element: Element)

    fun disconnect()
}

/**
 * Removes observer from elements.
 *
 * @param observer observer instance returned by [installVisibleSectionTabInteractionObserver]
 * @param sectionIds all ids of section
 *
 * @see installVisibleSectionTabInteractionObserver
 */
fun uninstallVisibleSectionTabInteractionObserver(
    observer: IntersectionObserver,
    @Suppress("UNUSED_PARAMETER") sectionIds: Iterable<String>
) {
    // TODO: [#480] - This function may not be necessary. It will be
    //  removed or implemented properly when #480 closed.
    observer.disconnect()
}

/**
 * Installs observer into `div`. Can contain headers (they have to have id
 * created by function [String.toSectionHeaderId] from section id) and text (those
 * `div`s use ids from [sectionIds].
 *
 * @param sectionsRoot root of div with text
 * @param sectionIds all ids of sections
 */
fun installVisibleSectionTabInteractionObserver(
    sectionsRoot: HTMLDivElement,
    sectionIds: List<String>
): IntersectionObserver {
    val options: dynamic = js("{rootMargin: '0px', threshold: 0.5}")
    options["root"] = sectionsRoot

    val tabStatesRegister = TabStatesRegister(sectionIds)
    val observer =
        IntersectionObserver { entries, _ ->
            entries.forEach {
                tabStatesRegister.updateIdState(it.target.id, it.isIntersecting)
            }
            tabStatesRegister.forEachTab { tabElementId, isSelected ->
                getElementById(tabElementId).let {
                    if (isSelected) {
                        it.addClass(AppStyleSheet.selected)
                    } else {
                        it.removeClass(AppStyleSheet.selected)
                    }
                }
            }
        }

    sectionIds.map { sectionId ->
        HeaderedSection(header = getElementById(sectionId.toSectionHeaderId()), section = getElementById(sectionId))
    }.forEach { headeredSection ->
        observer.observe(headeredSection.header)
        observer.observe(headeredSection.section)
    }

    return observer
}

private data class HeaderedSection(val header: Element, val section: Element)

/**
 * Helper method that returns element with element id. Throws [NullPointerException] with comment
 * when element can't be found.
 */
private fun getElementById(elementId: String) =
    requireNotNull(document.getElementById(elementId)) {
        "Unable to find element with id $elementId"
    }
